import axios from "axios";
import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { statusToast } from "../utils/notify";

import API from "../config.js";
const G    = "#FFB800";
const G2   = "#FFD700";
const GD   = "#C8860A";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

const PLANS = [
  { name: "Starter",  min: 50,    max: 499,    rate: 1.00, color: "#00C2FF", icon: "🌱" },
  { name: "Silver",   min: 500,   max: 1499,   rate: 1.10, color: "#C0C8D8", icon: "⚡" },
  { name: "Gold",     min: 1500,  max: 2999,   rate: 1.25, color: G2,        icon: "🥇" },
  { name: "Platinum", min: 3000,  max: 4999,   rate: 1.45, color: "#B8A0FF", icon: "💎" },
  { name: "Elite",    min: 5000,  max: null,   rate: 1.70, color: "#FF9900", icon: "👑" },
];

function getPlanForAmount(amt) {
  return PLANS.find(p => amt >= p.min && (p.max === null || amt <= p.max)) || null;
}

const fmt = v => (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Invest() {
  const [investment, setInvestment] = useState(null);
  const [balance,    setBalance]    = useState(0);
  const [amount,     setAmount]     = useState("");
  const [status,     setStatusRaw]  = useState(null);
  const setStatus = (s) => { setStatusRaw(s); statusToast(s); };
  const [loading,    setLoading]    = useState(false);
  const [wLoading,   setWLoading]   = useState(false);
  const [rLoading,   setRLoading]   = useState(false);
  const [pageLoad,   setPageLoad]   = useState(true);

  const token   = localStorage.getItem("token");
  const headers = { Authorization: "Bearer " + token };

  const parsedAmt   = parseFloat(String(amount).replace(/,/g, "").trim()) || 0;
  const previewPlan = getPlanForAmount(parsedAmt);

  useEffect(() => {
    if (!token) { window.location.href = "/"; return; }
    axios.get(`${API}/api/dashboard`, { headers })
      .then(res => {
        setBalance(res.data.balance ?? 0);
        setInvestment(res.data.investment || null);
      })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.removeItem("token"); window.location.href = "/"; }
      })
      .finally(() => setPageLoad(false));
  }, []);

  const handleReinvest = async () => {
    if (balance <= 0) return setStatus({ type: "error", msg: "No balance available to reinvest" });
    if (!window.confirm(`Reinvest entire wallet balance of ${fmt(balance)} USDT into investment?`)) return;
    setRLoading(true); setStatus(null);
    try {
      const res = await axios.post(`${API}/api/invest/reinvest`, {}, { headers });
      setStatus({ type: "success", msg: res.data.message });
      setInvestment(res.data.investment);
      setBalance(0);
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Reinvest failed" });
    } finally { setRLoading(false); }
  };

  const handleInvest = async () => {
    if (parsedAmt < 50) return setStatus({ type: "error", msg: "Minimum investment is 50 USDT" });
    if (parsedAmt > balance) return setStatus({ type: "error", msg: "Insufficient wallet balance" });
    setLoading(true); setStatus(null);
    try {
      const res = await axios.post(`${API}/api/invest`, { amount: parsedAmt }, { headers });
      if (res.data.message?.toLowerCase().includes("error") || res.data.message?.toLowerCase().includes("insufficient") || res.data.message?.toLowerCase().includes("minimum")) {
        setStatus({ type: "error", msg: res.data.message });
      } else {
        setStatus({ type: "success", msg: res.data.message });
        setInvestment(res.data.investment);
        setBalance(res.data.balance);
        setAmount("");
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Investment failed" });
    } finally { setLoading(false); }
  };

  const handleWithdrawInvestment = async () => {
    if (!window.confirm("Withdraw your full invested capital? Daily interest will stop.")) return;
    setWLoading(true); setStatus(null);
    try {
      const res = await axios.post(`${API}/api/invest/withdraw`, {}, { headers });
      setStatus({ type: "success", msg: res.data.message });
      setInvestment(null);
      setBalance(res.data.balance);
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Failed" });
    } finally { setWLoading(false); }
  };

  if (pageLoad) return (
    <div style={s.page}>
      <Navbar />
      <div style={s.loadWrap}>
        <div style={s.spinner} />
      </div>
    </div>
  );

  const activePlan = investment ? PLANS.find(p => p.name === investment.planName) || PLANS[0] : null;
  const dailyEst   = investment ? investment.amount * (activePlan?.rate / 100) : 0;
  const monthlyEst = dailyEst * 30;

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container} className="page-container">

        {/* ── Header ── */}
        <div style={s.header} className="page-header">
          <div>
            <h1 style={s.title}>Investment Center</h1>
            <p style={s.sub}>Grow your USDT with daily automated returns</p>
          </div>
          <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
            <div style={s.balancePill}>
              <span style={s.balLbl}>Wallet Balance</span>
              <span style={s.balVal}>{fmt(balance)} USDT</span>
            </div>
            {investment && (
              <div style={{...s.balancePill, borderColor:"rgba(255,184,0,0.3)"}}>
                <span style={s.balLbl}>Invested</span>
                <span style={{...s.balVal, color: activePlan?.color || G2}}>{fmt(investment.amount)} USDT</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Status ── */}
        {status && (
          <div style={{
            ...s.alert,
            background: status.type === "success" ? "rgba(0,232,122,0.07)" : "rgba(255,60,87,0.08)",
            border:     status.type === "success" ? "1px solid rgba(0,232,122,0.25)" : "1px solid rgba(255,60,87,0.3)",
            color:      status.type === "success" ? "#00e87a" : "#ff6b8a",
          }}>
            <span>{status.type === "success" ? "✓" : "⚠"}</span> {status.msg}
          </div>
        )}

        {/* ── Active Investment Card ── */}
        {investment && activePlan && (
          <div style={{...s.activeCard, borderColor: activePlan.color + "40"}}>
            <div style={s.acTopLine} />
            <div style={s.acHeader}>
              <div>
                <span style={s.acBadge}>{activePlan.icon} {activePlan.name} Plan</span>
                <div style={{...s.acRate, color: activePlan.color}}>{activePlan.rate}% / day</div>
              </div>
              <div style={s.acAmounts}>
                <div style={s.acAmtBox}>
                  <span style={s.acAmtLbl}>Invested</span>
                  <span style={{...s.acAmtVal, color: activePlan.color}}>{fmt(investment.amount)} USDT</span>
                </div>
                <div style={s.acAmtBox}>
                  <span style={s.acAmtLbl}>Total Earned</span>
                  <span style={{...s.acAmtVal, color: "#00e87a"}}>{fmt(investment.totalEarned)} USDT</span>
                </div>
              </div>
            </div>
            <div style={s.acStats}>
              <div style={s.acStat}>
                <span style={s.acStatLbl}>Daily Return</span>
                <span style={{...s.acStatVal, color: activePlan.color}}>+{fmt(dailyEst)} USDT</span>
              </div>
              <div style={s.acStat}>
                <span style={s.acStatLbl}>Monthly Est.</span>
                <span style={{...s.acStatVal, color: G2}}>+{fmt(monthlyEst)} USDT</span>
              </div>
              <div style={s.acStat}>
                <span style={s.acStatLbl}>Status</span>
                <span style={{...s.acStatVal, color: "#00e87a"}}>🟢 Active</span>
              </div>
              <div style={s.acStat}>
                <span style={s.acStatLbl}>Last Paid</span>
                <span style={s.acStatVal}>
                  {investment.lastPaidDate
                    ? new Date(investment.lastPaidDate).toLocaleDateString("en-US", { day: "2-digit", month: "short" })
                    : "Pending"}
                </span>
              </div>
            </div>
            <div style={{display:"flex", gap:10, padding:"0 20px 20px", flexWrap:"wrap"}}>
              <button
                style={{...s.reinvestBtn, opacity: rLoading ? 0.6 : 1, flex:1}}
                onClick={handleReinvest}
                disabled={rLoading}>
                {rLoading ? "Processing..." : `♻ Reinvest ${fmt(balance)} USDT`}
              </button>
              <button
                style={{...s.withdrawCapBtn, opacity: wLoading ? 0.6 : 1, flex:1, margin:0, padding:"11px 16px"}}
                onClick={handleWithdrawInvestment}
                disabled={wLoading}>
                {wLoading ? "Processing..." : "↙ Withdraw Capital"}
              </button>
            </div>
          </div>
        )}

        {/* ── Plan Cards Grid ── */}
        <div style={s.planGrid} className="plan-grid">
          {PLANS.map((plan, i) => {
            const isActive = investment?.planName === plan.name;
            const daily30  = (1000 * plan.rate / 100 * 30).toFixed(0);
            return (
              <div key={i} style={{
                ...s.planCard,
                borderColor: isActive ? plan.color : plan.color + "25",
                background:  isActive ? plan.color + "0D" : "rgba(8,18,40,0.85)",
                transform:   isActive ? "scale(1.03)" : "none",
              }}>
                {isActive && <div style={{...s.planTopLine, background: plan.color}} />}
                <div style={s.planIcon}>{plan.icon}</div>
                <div style={{...s.planName, color: plan.color}}>{plan.name}</div>
                <div style={{...s.planRate, color: plan.color}}>{plan.rate}%<span style={s.planRateSub}>/day</span></div>
                <div style={s.planRange}>
                  {plan.min.toLocaleString()} – {plan.max ? plan.max.toLocaleString() : "∞"} USDT
                </div>
                <div style={s.planEst}>
                  <span style={s.planEstLbl}>1000 USDT × 30 days</span>
                  <span style={{...s.planEstVal, color: plan.color}}>= {daily30} USDT</span>
                </div>
                {isActive && (
                  <div style={{...s.activeBadge, background: plan.color + "20", color: plan.color, borderColor: plan.color + "40"}}>
                    ✓ Your Active Plan
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Referral Chart ── */}
        <ReferralChart />

        {/* ── Invest Form ── */}
        <div style={s.formCard}>
          <div style={s.formTopLine} />
          <h2 style={s.formTitle}>{investment ? "Top-Up Investment" : "Start Investing"}</h2>
          <p style={s.formSub}>
            {investment
              ? `Adding more will upgrade your plan based on total invested amount.`
              : `Enter amount to invest. Minimum 50 USDT.`}
          </p>

          <div style={s.inputWrap}>
            <span style={s.inputPrefix}>USDT</span>
            <input
              type="number"
              min="50"
              step="any"
              placeholder="Enter amount (min 50)"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={s.input}
            />
          </div>

          {parsedAmt >= 50 && previewPlan && (
            <div style={{...s.preview, borderColor: previewPlan.color + "35"}}>
              <div style={s.previewRow}>
                <span style={s.previewLbl}>Plan</span>
                <span style={{...s.previewVal, color: previewPlan.color}}>{previewPlan.icon} {previewPlan.name}</span>
              </div>
              <div style={s.previewRow}>
                <span style={s.previewLbl}>Daily Rate</span>
                <span style={{...s.previewVal, color: previewPlan.color}}>{previewPlan.rate}% / day</span>
              </div>
              <div style={s.previewRow}>
                <span style={s.previewLbl}>Daily Earn</span>
                <span style={{...s.previewVal, color: "#00e87a"}}>+{fmt(parsedAmt * previewPlan.rate / 100)} USDT</span>
              </div>
              <div style={s.previewRow}>
                <span style={s.previewLbl}>30-Day Est.</span>
                <span style={{...s.previewVal, color: G2}}>+{fmt(parsedAmt * previewPlan.rate / 100 * 30)} USDT</span>
              </div>
            </div>
          )}

          <button
            style={{...s.investBtn, opacity: loading ? 0.65 : 1, cursor: loading ? "not-allowed" : "pointer"}}
            onClick={handleInvest}
            disabled={loading}>
            {loading ? <span style={s.spinner2} /> : `${investment ? "Add to" : "Start"} Investment →`}
          </button>

          <div style={s.note}>
            💡 Interest is credited to your wallet daily. Withdraw capital anytime.
          </div>
        </div>

      </div>
    </div>
  );
}

/* ── Referral MLM Chart ── */
// Two income types shown in chart:
// 1. Deposit bonus (one-time) — L1: 10%, L2: 5% ... L10: 0.3%
// 2. Investment daily income — L1: 0.2%, L2: 0.15% ... L10: 0.03%
const LEVELS = [
  { level: 1,  depositPct: 10.0, investPct: 0.20, color: "#FFD700", label: "Direct Referral" },
  { level: 2,  depositPct:  5.0, investPct: 0.15, color: "#FFB800", label: "Level 2" },
  { level: 3,  depositPct:  3.0, investPct: 0.10, color: "#FF9900", label: "Level 3" },
  { level: 4,  depositPct:  2.0, investPct: 0.05, color: "#00C2FF", label: "Level 4" },
  { level: 5,  depositPct:  1.0, investPct: 0.03, color: "#00C2FF", label: "Level 5" },
  { level: 6,  depositPct:  1.0, investPct: 0.03, color: "#B8A0FF", label: "Level 6" },
  { level: 7,  depositPct:  0.5, investPct: 0.03, color: "#B8A0FF", label: "Level 7" },
  { level: 8,  depositPct:  0.5, investPct: 0.03, color: "#C0C8D8", label: "Level 8" },
  { level: 9,  depositPct:  0.3, investPct: 0.03, color: "#C0C8D8", label: "Level 9" },
  { level: 10, depositPct:  0.3, investPct: 0.03, color: "#C0C8D8", label: "Level 10" },
];
const MAX_PCT = 10;

function ReferralChart() {
  const [tab, setTab] = useState("deposit"); // "deposit" | "invest"

  /* Local timezone credit time */
  const localCreditTime = (() => {
    try {
      const utcMidnight = new Date();
      utcMidnight.setUTCHours(0, 0, 0, 0);
      return utcMidnight.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch { return "12:00 AM"; }
  })();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

  return (
    <div style={rc.wrap}>
      <div style={rc.topLine} />
      <div style={rc.inner}>
        {/* Header */}
        <div style={rc.head}>
          <div>
            <div style={rc.badge}>REFERRAL INCOME</div>
            <h2 style={rc.title}>Multi-Level Commission</h2>
            <p style={rc.sub}>Earn on deposits + daily investment returns — up to 10 levels deep</p>
          </div>
          <div style={rc.timePill}>
            <span style={rc.timeLbl}>Investment income daily at</span>
            <span style={rc.timeVal}>🕛 {localCreditTime}</span>
            {tz && <span style={rc.timeTz}>{tz}</span>}
          </div>
        </div>

        {/* Tab switch */}
        <div style={rc.tabRow}>
          <button
            style={{ ...rc.tabBtn, ...(tab === "deposit" ? rc.tabActive : {}) }}
            onClick={() => setTab("deposit")}>
            💰 Deposit Bonus (one-time)
          </button>
          <button
            style={{ ...rc.tabBtn, ...(tab === "invest" ? rc.tabActive : {}) }}
            onClick={() => setTab("invest")}>
            📈 Investment Income (daily)
          </button>
        </div>

        {/* Bar Chart */}
        <div style={rc.chartArea}>
          {LEVELS.map(({ level, depositPct, investPct, color, label }) => {
            const pct  = tab === "deposit" ? depositPct : investPct;
            const barW = `${(pct / MAX_PCT) * 100}%`;
            return (
              <div key={level} style={rc.row}>
                <div style={rc.rowLabel}>
                  <span style={{ ...rc.lvlBadge, background: color + "18", color, borderColor: color + "35" }}>
                    L{level}
                  </span>
                  <span style={rc.lvlDesc}>{label}</span>
                </div>
                <div style={rc.barTrack}>
                  <div style={{ ...rc.barFill, width: barW, background: `linear-gradient(90deg, ${color}CC, ${color}55)`, boxShadow: `0 0 10px ${color}40` }} />
                </div>
                <span style={{ ...rc.pctLabel, color }}>{pct}%{tab === "invest" ? "/day" : ""}</span>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={rc.footer}>
          <span style={rc.footerDot} />
          {tab === "deposit"
            ? "Deposit bonus paid instantly when your referral deposits."
            : "Investment income credited daily at 12:00 AM UTC to your wallet."}
          &nbsp;&nbsp;
          <span style={rc.footerDot} />
          No limit on referrals — the more you refer, the more you earn.
        </div>
      </div>
    </div>
  );
}

const rc = {
  tabRow: { display:"flex", gap:8, marginBottom:20 },
  tabBtn: {
    flex:1, padding:"10px 14px",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight:600, fontSize:12,
    color:"rgba(160,180,220,0.5)",
    background:"rgba(0,0,0,0.3)",
    border:"1px solid rgba(255,184,0,0.1)",
    borderRadius:8, cursor:"pointer", letterSpacing:0.3,
    transition:"all 0.2s",
  },
  tabActive: {
    color:"#FFD700",
    background:"rgba(255,184,0,0.08)",
    border:"1px solid rgba(255,184,0,0.3)",
  },
  wrap: {
    background: "rgba(8,18,40,0.95)",
    border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 16, overflow: "hidden",
    marginBottom: 28,
    boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
  },
  topLine: { height: 2, background: `linear-gradient(90deg, transparent, #FFD700, #FFB800, #C8860A, transparent)` },
  inner: { padding: "28px 32px" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 28 },
  badge: {
    display: "inline-block",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, fontWeight: 700,
    color: "#FFB800", background: "rgba(255,184,0,0.1)",
    border: "1px solid rgba(255,184,0,0.25)",
    borderRadius: 20, padding: "3px 10px", letterSpacing: 1.5,
    marginBottom: 8,
  },
  title: {
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 18,
    color: "#e0e8ff", letterSpacing: 1, marginBottom: 6,
  },
  sub: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 13, color: "rgba(160,180,220,0.5)" },
  timePill: {
    background: "rgba(0,194,255,0.06)",
    border: "1px solid rgba(0,194,255,0.2)",
    borderRadius: 12, padding: "12px 18px", textAlign: "center",
    minWidth: 160,
  },
  timeLbl: { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, color: "rgba(0,194,255,0.5)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 },
  timeVal: { display: "block", fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 16, color: "#00C2FF", marginBottom: 4 },
  timeTz:  { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, color: "rgba(0,194,255,0.4)" },

  chartArea: { display: "flex", flexDirection: "column", gap: 10 },
  row: { display: "flex", alignItems: "center", gap: 12 },
  rowLabel: { display: "flex", alignItems: "center", gap: 8, width: 160, flexShrink: 0 },
  lvlBadge: {
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 10,
    border: "1px solid", borderRadius: 6, padding: "3px 8px",
    flexShrink: 0,
  },
  lvlDesc: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12, color: "rgba(160,180,220,0.5)", whiteSpace: "nowrap" },
  barTrack: {
    flex: 1, height: 10, borderRadius: 99,
    background: "rgba(255,255,255,0.04)",
    overflow: "hidden",
  },
  barFill: { height: "100%", borderRadius: 99, transition: "width 1s ease" },
  pctLabel: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12, width: 40, textAlign: "right", flexShrink: 0 },
  footer: {
    marginTop: 20, paddingTop: 16,
    borderTop: "1px solid rgba(255,184,0,0.07)",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12,
    color: "rgba(160,180,220,0.4)", lineHeight: 1.6,
    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
  },
  footerDot: {
    display: "inline-block", width: 5, height: 5,
    borderRadius: "50%", background: "#FFB800", flexShrink: 0,
  },
};

/* ── Styles ── */
const s = {
  page:      { minHeight: "100vh", background: "#060D1A" },
  container: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px" },
  loadWrap:  { display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" },

  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-end", marginBottom: 28, flexWrap: "wrap", gap: 12,
  },
  title: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 26,
    background: `linear-gradient(135deg, #f0e0a0, ${G}, ${GD})`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    letterSpacing: 2, marginBottom: 6,
  },
  sub: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 14, color: "rgba(200,160,60,0.55)" },
  balancePill: {
    background: "rgba(10,8,3,0.95)",
    border: `1px solid rgba(255,184,0,0.2)`,
    borderRadius: 10, padding: "12px 20px", textAlign: "right",
  },
  balLbl: { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 10, color: "rgba(200,160,60,0.45)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 },
  balVal: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 18, color: G },

  alert: {
    display: "flex", alignItems: "center", gap: 10,
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 14,
    padding: "12px 16px", borderRadius: 8,
    marginBottom: 20, borderWidth: 1,
  },

  /* Active investment card */
  activeCard: {
    background: "rgba(8,18,40,0.95)",
    border: "1px solid",
    borderRadius: 16, marginBottom: 28, overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
  },
  acTopLine: { height: 3, background: GRAD },
  acHeader: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "24px 28px 16px",
    flexWrap: "wrap", gap: 16,
  },
  acBadge: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 13, color: G2,
    letterSpacing: 1, display: "block", marginBottom: 6,
  },
  acRate: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 900, fontSize: 32, letterSpacing: 1,
  },
  acAmounts: { display: "flex", gap: 24 },
  acAmtBox: { textAlign: "right" },
  acAmtLbl: { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 10, color: "rgba(160,180,220,0.45)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 },
  acAmtVal: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 18 },

  acStats: {
    display: "grid", gridTemplateColumns: "repeat(4,1fr)",
    gap: 0, borderTop: "1px solid rgba(255,184,0,0.08)",
  },
  acStat: {
    padding: "16px 20px", textAlign: "center",
    borderRight: "1px solid rgba(255,184,0,0.07)",
  },
  acStatLbl: { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 10, color: "rgba(160,180,220,0.4)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 },
  acStatVal: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 14, color: "rgba(224,232,255,0.85)" },

  reinvestBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 13,
    color: "#00e87a",
    background: "rgba(0,232,122,0.08)",
    border: "1px solid rgba(0,232,122,0.25)",
    borderRadius: 8, padding: "11px 16px",
    cursor: "pointer", letterSpacing: 0.5,
    transition: "background 0.2s",
  },
  withdrawCapBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontWeight: 600, fontSize: 13,
    color: "#ff6b8a",
    background: "rgba(255,60,87,0.07)",
    border: "1px solid rgba(255,60,87,0.15)",
    borderRadius: 8,
    padding: "14px",
    cursor: "pointer", letterSpacing: 0.5,
    transition: "background 0.2s",
  },

  /* Plan grid */
  planGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 14, marginBottom: 28,
  },
  planCard: {
    border: "1px solid",
    borderRadius: 14, padding: "24px 18px",
    textAlign: "center", transition: "all 0.25s",
    position: "relative", overflow: "hidden",
    backdropFilter: "blur(16px)",
  },
  planTopLine: { position: "absolute", top: 0, left: 0, right: 0, height: 2 },
  planIcon:    { fontSize: 36, marginBottom: 10 },
  planName: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 13, letterSpacing: 1.5,
    textTransform: "uppercase", marginBottom: 10,
  },
  planRate: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 900, fontSize: 30, letterSpacing: 1, marginBottom: 4,
  },
  planRateSub: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 13, fontWeight: 400, opacity: 0.7 },
  planRange: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(160,180,220,0.5)", marginBottom: 14,
  },
  planEst: {
    background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: "10px 12px",
    marginBottom: 12,
  },
  planEstLbl: { display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 10, color: "rgba(160,180,220,0.4)", marginBottom: 4 },
  planEstVal: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 13 },
  activeBadge: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontWeight: 700, fontSize: 10,
    border: "1px solid", borderRadius: 20,
    padding: "4px 12px", letterSpacing: 0.5,
    display: "inline-block", marginTop: 4,
  },

  /* Form card */
  formCard: {
    background: "rgba(8,18,40,0.95)",
    border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 16, overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
  },
  formTopLine: { height: 2, background: GRAD },
  formTitle: {
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 18, color: "#e0e8ff",
    letterSpacing: 1, padding: "24px 28px 4px",
  },
  formSub: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 13, color: "rgba(160,180,220,0.5)",
    padding: "0 28px 20px",
  },
  inputWrap: {
    display: "flex", alignItems: "center",
    background: "rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,184,0,0.15)",
    borderRadius: 10, margin: "0 28px 16px",
    overflow: "hidden",
  },
  inputPrefix: {
    padding: "14px 16px",
    fontFamily: "'Figtree', sans-serif",
    fontSize: 11, color: G, fontWeight: 700,
    background: "rgba(255,184,0,0.06)",
    borderRight: "1px solid rgba(255,184,0,0.12)",
    whiteSpace: "nowrap",
  },
  input: {
    flex: 1, padding: "14px 16px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 15, color: "#e0e8ff",
    background: "transparent",
    border: "none", outline: "none",
  },
  preview: {
    margin: "0 28px 20px",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid",
    borderRadius: 10, padding: "16px 18px",
  },
  previewRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "6px 0",
    borderBottom: "1px solid rgba(255,184,0,0.05)",
  },
  previewLbl: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif", fontSize: 12, color: "rgba(160,180,220,0.5)" },
  previewVal: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 13 },

  investBtn: {
    display: "block", width: "calc(100% - 56px)",
    margin: "0 28px 16px",
    fontFamily: "'Figtree', sans-serif",
    fontWeight: 700, fontSize: 13,
    color: "#060D1A", background: GRAD,
    border: "none", borderRadius: 10,
    padding: "15px", letterSpacing: 1,
    boxShadow: "0 4px 24px rgba(255,184,0,0.4)",
  },
  note: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree', sans-serif",
    fontSize: 12, color: "rgba(160,180,220,0.4)",
    textAlign: "center", padding: "0 28px 24px",
    lineHeight: 1.6,
  },

  spinner: {
    width: 40, height: 40,
    border: "3px solid rgba(255,184,0,0.15)",
    borderTop: `3px solid ${G}`,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "auto",
  },
  spinner2: {
    display: "inline-block",
    width: 18, height: 18,
    border: "2px solid rgba(0,0,0,0.3)",
    borderTop: "2px solid #060D1A",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    verticalAlign: "middle",
  },
};




